import { useState, FormEvent, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { joinSignup } from '../utils/api';
import BrandLogo from '../components/BrandLogo';

interface Particle {
    x: number;
    y: number;
    size: number;
    speedX: number;
    speedY: number;
    opacity: number;
    hue: number;
}

const AnimatedBackground = () => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const particlesRef = useRef<Particle[]>([]);
    const animationRef = useRef<number>();

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const resize = () => {
            canvas.width = window.innerWidth;
            canvas.height = window.innerHeight;
        };

        resize();
        window.addEventListener('resize', resize);

        const createParticles = () => {
            const particles: Particle[] = [];
            const particleCount = Math.min(80, Math.floor((canvas.width * canvas.height) / 15000));

            for (let i = 0; i < particleCount; i++) {
                particles.push({
                    x: Math.random() * canvas.width,
                    y: Math.random() * canvas.height,
                    size: Math.random() * 2 + 0.5,
                    speedX: (Math.random() - 0.5) * 0.3,
                    speedY: (Math.random() - 0.5) * 0.3,
                    opacity: Math.random() * 0.5 + 0.2,
                    hue: Math.random() > 0.5 ? 45 : 210
                });
            }
            return particles;
        };

        particlesRef.current = createParticles();

        const animate = () => {
            ctx.clearRect(0, 0, canvas.width, canvas.height);

            particlesRef.current.forEach((particle) => {
                particle.x += particle.speedX;
                particle.y += particle.speedY;

                if (particle.x < 0) particle.x = canvas.width;
                if (particle.x > canvas.width) particle.x = 0;
                if (particle.y < 0) particle.y = canvas.height;
                if (particle.y > canvas.height) particle.y = 0;

                ctx.beginPath();
                ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
                ctx.fillStyle = `hsla(${particle.hue}, 70%, 60%, ${particle.opacity})`;
                ctx.fill();
            });

            animationRef.current = requestAnimationFrame(animate);
        };

        animate();

        return () => {
            window.removeEventListener('resize', resize);
            if (animationRef.current) {
                cancelAnimationFrame(animationRef.current);
            }
        };
    }, []);

    return <canvas ref={canvasRef} className="hero-canvas" />;
};

export default function JoinPage() {
    const navigate = useNavigate();
    const [socialHandle, setSocialHandle] = useState('');
    const [email, setEmail] = useState('');
    const [referralCode, setReferralCode] = useState('');
    const [interests, setInterests] = useState<string[]>([]);
    const [showAllInterests, setShowAllInterests] = useState(false);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const INTERESTS_OPTIONS = [
        'Campaign Participation',
        'Airdrop Opportunities',
        'Referral Programs',
        'Community Activities',
        'Web3 Tools',
        'Early Project Access',
        'Ambassador Opportunities',
        'Educational Content'
    ];

    const displayedInterests = showAllInterests ? INTERESTS_OPTIONS : INTERESTS_OPTIONS.slice(0, 2);

    const validateEmail = (email: string) => {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    };

    const handleInterestChange = (option: string) => {
        setInterests(prev =>
            prev.includes(option)
                ? prev.filter(i => i !== option)
                : [...prev, option]
        );
    };

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        setError('');
        setSuccessMessage('');

        if (!socialHandle.trim()) {
            setError('Please enter your Telegram or X handle');
            return;
        }

        if (!email.trim()) {
            setError('Please enter your email address');
            return;
        }

        if (!validateEmail(email)) {
            setError('Please enter a valid email address');
            return;
        }

        setIsLoading(true);

        try {
            const response = await joinSignup(socialHandle.trim(), email.trim(), referralCode.trim(), interests);

            if (response.success) {
                setSuccessMessage(response.message || 'Successfully joined the waitlist!');
                setSocialHandle('');
                setEmail('');
                setReferralCode('');
                setInterests([]);
            } else {
                setError(response.message || 'Failed to join. Please try again.');
            }
        } catch (err) {
            setError('Connection error. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="landing-page join-page">
            <AnimatedBackground />
            <div className="hero-gradient-overlay" />

            <nav className="top-navbar">
                <div className="navbar-container">
                    <div className="navbar-logo" onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>
                        <BrandLogo size={36} />
                        <div className="navbar-logo-text">
                            <span>
                                web3lab
                                <span style={{ fontSize: '0.45em', opacity: 0.8, marginLeft: '2px', position: 'relative', top: '-2px' }}>.online</span>
                            </span>
                            <span className="navbar-logo-subtitle">training</span>
                        </div>
                    </div>
                    <button className="btn btn-outline btn-sm" onClick={() => navigate('/')}>
                        Back to Home
                    </button>
                </div>
            </nav>

            <div className="landing-split">
                <div className="landing-hero">
                    <div className="hero-content">
                        <div className="hero-badge">
                            <span className="badge-dot"></span>
                            Beta Platform
                        </div>

                        <h1 className="hero-title">
                            Be Part of the<br />
                            <span className="text-gradient">Web3 Revolution</span>
                        </h1>

                        <p className="hero-subtitle">
                            Join our exclusive community and get early access to training and tools.
                        </p>

                        <div className="trust-section">
                            <div className="trust-badges">
                                <div className="trust-badge">
                                    <span className="badge-icon">🌟</span>
                                    <span>Early Access</span>
                                </div>
                                <div className="trust-badge">
                                    <span className="badge-icon">🤝</span>
                                    <span>Community Support</span>
                                </div>
                                <div className="trust-badge">
                                    <span className="badge-icon">⚡</span>
                                    <span>Fast Growth</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="hero-decoration">
                        <div className="deco-circle deco-circle-1"></div>
                        <div className="deco-circle deco-circle-2"></div>
                        <div className="deco-circle deco-circle-3"></div>
                    </div>
                </div>

                <div className="landing-form-section">
                    <div className="landing-form-container">
                        <div className="form-header">
                            <h2>Join Us</h2>
                            <p>Fill in your details to get started</p>
                        </div>

                        <form onSubmit={handleSubmit}>
                            <div className="input-group">
                                <label htmlFor="socialHandle" className="input-label">
                                    Telegram or X (Twitter)
                                </label>
                                <input
                                    type="text"
                                    id="socialHandle"
                                    className={`input ${error && !socialHandle ? 'input-error' : ''}`}
                                    placeholder="@username"
                                    value={socialHandle}
                                    onChange={(e) => setSocialHandle(e.target.value)}
                                    disabled={isLoading}
                                    autoComplete="off"
                                    autoFocus
                                />
                            </div>

                            <div className="input-group" style={{ marginTop: 'var(--spacing-md)' }}>
                                <label htmlFor="email" className="input-label">
                                    Email Address
                                </label>
                                <input
                                    type="email"
                                    id="email"
                                    className={`input ${error && (!email || !validateEmail(email)) ? 'input-error' : ''}`}
                                    placeholder="name@example.com"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    disabled={isLoading}
                                    autoComplete="email"
                                />
                            </div>

                            <div className="input-group-optional" style={{ marginTop: 'var(--spacing-md)' }}>
                                <label htmlFor="referralCode" className="input-label" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>Referral Code</span>
                                    <span style={{
                                        fontSize: '0.7rem',
                                        color: 'var(--text-secondary)',
                                        background: 'rgba(255,255,255,0.05)',
                                        padding: '2px 8px',
                                        borderRadius: '4px',
                                        fontWeight: 500
                                    }}>Optional</span>
                                </label>
                                <input
                                    type="text"
                                    id="referralCode"
                                    className="input"
                                    placeholder="Enter referral code"
                                    value={referralCode}
                                    onChange={(e) => setReferralCode(e.target.value)}
                                    disabled={isLoading}
                                    autoComplete="off"
                                />
                            </div>

                            <div className="input-group-optional" style={{ marginTop: 'var(--spacing-md)' }}>
                                <div className="input-label" style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                                    <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>Select the areas you're most interested in</span>
                                    <span style={{
                                        fontSize: '0.7rem',
                                        color: 'var(--text-secondary)',
                                        background: 'rgba(255,255,255,0.05)',
                                        padding: '2px 8px',
                                        borderRadius: '4px',
                                        fontWeight: 500
                                    }}>Optional</span>
                                </div>
                                <div className="interests-grid" style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                                    gap: '10px'
                                }}>
                                    {displayedInterests.map((option) => (
                                        <label
                                            key={option}
                                            className="interest-checkbox"
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '10px',
                                                padding: '10px 12px',
                                                background: interests.includes(option) ? 'rgba(99, 102, 241, 0.15)' : 'rgba(255,255,255,0.03)',
                                                border: interests.includes(option) ? '1px solid rgba(99, 102, 241, 0.5)' : '1px solid rgba(255,255,255,0.1)',
                                                borderRadius: '8px',
                                                cursor: 'pointer',
                                                transition: 'all 0.2s ease',
                                                fontSize: '0.85rem',
                                                color: interests.includes(option) ? 'var(--text-primary)' : 'var(--text-secondary)'
                                            }}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={interests.includes(option)}
                                                onChange={() => handleInterestChange(option)}
                                                disabled={isLoading}
                                                style={{ display: 'none' }}
                                            />
                                            <span style={{
                                                width: '18px',
                                                height: '18px',
                                                borderRadius: '4px',
                                                border: interests.includes(option) ? '2px solid #6366f1' : '2px solid rgba(255,255,255,0.3)',
                                                background: interests.includes(option) ? '#6366f1' : 'transparent',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                flexShrink: 0,
                                                transition: 'all 0.2s ease'
                                            }}>
                                                {interests.includes(option) && (
                                                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                                                        <path d="M10 3L4.5 8.5L2 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                    </svg>
                                                )}
                                            </span>
                                            <span>{option}</span>
                                        </label>
                                    ))}
                                </div>
                                {!showAllInterests && INTERESTS_OPTIONS.length > 2 && (
                                    <button
                                        type="button"
                                        onClick={() => setShowAllInterests(true)}
                                        style={{
                                            marginTop: '12px',
                                            background: 'transparent',
                                            border: 'none',
                                            color: '#6366f1',
                                            fontSize: '0.85rem',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '4px',
                                            padding: '4px 0'
                                        }}
                                    >
                                        Show more ({INTERESTS_OPTIONS.length - 2} more)
                                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                                            <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                    </button>
                                )}
                                {showAllInterests && (
                                    <button
                                        type="button"
                                        onClick={() => setShowAllInterests(false)}
                                        style={{
                                            marginTop: '12px',
                                            background: 'transparent',
                                            border: 'none',
                                            color: '#6366f1',
                                            fontSize: '0.85rem',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '4px',
                                            padding: '4px 0'
                                        }}
                                    >
                                        Show less
                                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                                            <path d="M9 7.5L6 4.5L3 7.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                    </button>
                                )}
                            </div>

                            {error && (
                                <div className="alert alert-error animate-fade-in" style={{ marginTop: 'var(--spacing-md)' }}>
                                    <span className="alert-icon">⚠️</span>
                                    <span>{error}</span>
                                </div>
                            )}

                            {successMessage && (
                                <div className="alert alert-success animate-fade-in" style={{ marginTop: 'var(--spacing-md)' }}>
                                    <span className="alert-icon">✅</span>
                                    <span>{successMessage}</span>
                                </div>
                            )}

                            <button
                                type="submit"
                                className="btn btn-primary btn-block btn-lg"
                                style={{ marginTop: 'var(--spacing-lg)' }}
                                disabled={isLoading}
                            >
                                {isLoading ? (
                                    <>
                                        <div className="spinner"></div>
                                        <span>Joining...</span>
                                    </>
                                ) : (
                                    <span>Join Waitlist</span>
                                )}
                            </button>
                        </form>

                        <div className="form-notice" style={{ marginTop: 'var(--spacing-xl)' }}>
                            <span className="notice-icon">ℹ️</span>
                            <p>
                                By joining, you'll receive updates about our platform and exclusive early access opportunities.
                            </p>
                        </div>

                        <div className="form-footer">
                            <p>Already have an access code? <a href="/" onClick={(e) => { e.preventDefault(); navigate('/'); }}>Go to Login</a></p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
